import Link from 'next/link';
import { ArrowDown, Loader2 } from 'lucide-react';

type SwapTextButtonVariant = 'filled' | 'outline';

/** `light` = white surface + black label (product hero CTAs). Default `brand` = red filled. */
type SwapTextButtonTone = 'brand' | 'light';

type SwapTextButtonArrowDirection = 'diagonal' | 'down';

interface SwapTextButtonProps {
  text: string;
  hoverText?: string;
  variant?: SwapTextButtonVariant;
  /** When `light`, filled styling becomes white/black regardless of `variant`. */
  tone?: SwapTextButtonTone;
  showArrow?: boolean;
  /** Default `diagonal` (top-right). Use `down` for scroll / anchor CTAs. */
  arrowDirection?: SwapTextButtonArrowDirection;
  href?: string;
  target?: string;
  rel?: string;
  download?: boolean | string;
  className?: string;
  'aria-label'?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
}

export default function SwapTextButton({
  text,
  hoverText,
  variant = 'filled',
  tone = 'brand',
  showArrow = true,
  arrowDirection = 'diagonal',
  href,
  target,
  rel,
  download,
  className,
  'aria-label': ariaLabel,
  onClick,
  type = 'button',
  disabled = false,
  loading = false,
}: SwapTextButtonProps) {
  const isDisabled = disabled || loading;
  const rootBase =
    'group relative inline-flex items-center gap-[10px] whitespace-nowrap rounded-lg px-4 py-3 text-sm md:text-md leading-[1.26] [html:lang(hi)_&]:leading-[1.42] transition-colors shadow-lg min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';

  const rootVariant =
    tone === 'light'
      ? 'border-0 bg-white text-black shadow-md hover:bg-neutral-100 hover:text-black focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111111]'
      : variant === 'outline'
        ? 'bg-white text-red-600 border border-red-600 hover:bg-red-600 hover:text-white focus:ring-2 focus:ring-red-600 focus:ring-offset-2'
        : 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2';

  const rootClassName = `${rootBase} ${rootVariant} ${className ?? ''}`.trim();
  const safeRel = rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined);

  const content = loading ? (
    <>
      <Loader2
        className="h-[18px] w-[18px] flex-shrink-0 animate-spin text-current"
        aria-hidden="true"
      />
      <span className="whitespace-nowrap">{text}</span>
    </>
  ) : (
    <>
      {/* Text Structure */}
      <span className="relative inline-grid items-center [&>*]:col-start-1 [&>*]:row-start-1">
        <span className="invisible whitespace-nowrap" aria-hidden>
          {(hoverText ?? text).length > text.length ? (hoverText ?? text) : text}
        </span>

        <span className="text-original flex overflow-hidden whitespace-nowrap">
          <span className="transition-transform duration-200 ease-out group-hover:translate-y-[1.5em]">
            {text}
          </span>
        </span>

        <span className="text-hover flex overflow-hidden whitespace-nowrap pointer-events-none">
          <span className="transition-transform duration-200 ease-out -translate-y-[1.5em] group-hover:translate-y-0">
            {hoverText ?? text}
          </span>
        </span>
      </span>

      {showArrow && (
        <div className="relative grid h-[18px] w-[18px] flex-shrink-0 place-items-center overflow-hidden rounded-full bg-transparent">
          <div
            className={[
              'absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-in-out',
              arrowDirection === 'down'
                ? 'group-hover:translate-y-[150%]'
                : 'group-hover:translate-x-[150%] group-hover:-translate-y-[150%]',
            ].join(' ')}
          >
            {arrowDirection === 'down' ? (
              <ArrowDown className="h-[14px] w-[14px] flex-shrink-0" aria-hidden="true" />
            ) : (
              <DiagonalArrowIcon />
            )}
          </div>

          <div
            className={[
              'absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-in-out group-hover:translate-x-0 group-hover:translate-y-0 group-hover:delay-[100ms]',
              arrowDirection === 'down'
                ? '-translate-y-[150%]'
                : 'translate-x-[-150%] translate-y-[150%]',
            ].join(' ')}
          >
            {arrowDirection === 'down' ? (
              <ArrowDown className="h-[16px] w-[16px] flex-shrink-0" aria-hidden="true" />
            ) : (
              <DiagonalArrowIcon />
            )}
          </div>
        </div>
      )}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        className={rootClassName}
        target={target}
        rel={safeRel}
        download={download}
        aria-label={ariaLabel}
        onClick={onClick}
        aria-disabled={isDisabled || undefined}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={rootClassName}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={isDisabled}
      aria-busy={loading || undefined}
    >
      {content}
    </button>
  );
}

function DiagonalArrowIcon() {
  return (
    <svg
      viewBox="0 0 14 15"
      width="10"
      height="15"
      className="block text-current"
      aria-hidden="true"
    >
      <path
        d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
        fill="currentColor"
      />
    </svg>
  );
}
